class BanzrcGtFinale extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.configurator = document.querySelector('banzrc-gt-configurator');
    this.purchaseSection = this.configurator?.closest('.shopify-section');
    this.rows = [...this.querySelectorAll('[data-finale-variant]')].map((row) => ({ ...row.dataset }));
    this.sceneButtons = [...this.querySelectorAll('[data-finale-scene]')];

    this.sceneButtons.forEach((button) => button.addEventListener('click', () => {
      if (matchMedia('(max-width: 749px)').matches && this.configurator) {
        const colorButton = [...this.configurator.querySelectorAll('[data-color]')].find((item) => item.dataset.color.trim().toLowerCase() === button.dataset.finaleScene.trim().toLowerCase());
        if (colorButton) colorButton.click();
      }
      this.showScene(button);
    }));
    this.querySelector('[data-finale-change]')?.addEventListener('click', () => this.goToConfiguration());
    this.querySelector('[data-finale-chat]')?.addEventListener('click', () => this.openChat());
    this.querySelector('[data-finale-cart]')?.addEventListener('click', () => this.purchase());
    this.querySelector('[data-sticky-cart]')?.addEventListener('click', () => this.purchase());
    this.chatHost = document.querySelector('shopify-chat');
    if (this.chatHost) {
      this.chatStateObserver = new MutationObserver(() => {
        this.dataset.mobileChatOpen = String(this.chatHost.hasAttribute('open'));
      });
      this.chatStateObserver.observe(this.chatHost, { attributes:true, attributeFilter:['open'] });
    }

    if (this.configurator) {
      this.configObserver = new MutationObserver(() => this.render());
      this.configObserver.observe(this.configurator, { attributes:true, attributeFilter:['data-selection-complete'] });
      this.configurator.querySelectorAll('[data-color],[data-power]').forEach((button) => button.addEventListener('click', () => queueMicrotask(() => this.render())));
    }
    this.purchaseObserver = new IntersectionObserver(([entry]) => {
      this.purchasePassed = entry.boundingClientRect.bottom < 0;
      this.updateSticky();
    }, { threshold:0 });
    if (this.purchaseSection) this.purchaseObserver.observe(this.purchaseSection);
    this.finaleObserver = new IntersectionObserver(([entry]) => {
      this.finaleVisible = entry.isIntersecting;
      this.dataset.mobileInView = String(entry.isIntersecting);
      this.updateSticky();
    }, { threshold:.05 });
    const finaleStage = this.querySelector('.banzrc-gt-finale__stage');
    if (finaleStage) this.finaleObserver.observe(finaleStage);
    this.render();
  }

  get state() {
    const color = this.configurator?.selectedColor || null;
    const power = this.configurator?.selectedPower || null;
    const normalize = (value) => String(value || '').trim().toLocaleLowerCase();
    const row = this.rows.find((item) => normalize(item.color) === normalize(color) && normalize(item.power) === normalize(power));
    return { color, power, row, ready:Boolean(row && row.available === 'true') };
  }

  render() {
    const { color, power, row, ready } = this.state;
    const chooseColorPower = this.dataset.chooseColorPower || 'Choose color and power';
    const chooseConfiguration = this.dataset.chooseConfiguration || 'Choose configuration';
    const addToCart = this.dataset.addToCart || 'Add to cart';
    const config = ready ? `${color} · ${power}` : [color,power].filter(Boolean).join(' · ') || chooseColorPower;
    const price = ready ? row.price : chooseConfiguration;
    this.querySelector('[data-finale-config]').textContent = config;
    this.querySelector('[data-finale-price]').textContent = price;
    this.querySelector('[data-sticky-config]').textContent = config;
    this.querySelector('[data-sticky-price]').textContent = ready ? price : '—';
    this.querySelector('[data-finale-cart]').textContent = ready ? addToCart : chooseConfiguration;
    this.querySelector('[data-sticky-cart]').textContent = ready ? addToCart : chooseConfiguration;
    if (color) {
      const normalizedColor = String(color).trim().toLocaleLowerCase();
      const scene = this.sceneButtons.find((button) => String(button.dataset.finaleScene || '').trim().toLocaleLowerCase() === normalizedColor);
      if (scene) this.showScene(scene);
    }
  }

  showScene(button) {
    this.sceneButtons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
    const tone = button.dataset.finaleTone || 'grey';
    if (this.dataset.finaleTone !== tone) {
      this.dataset.finaleTone = tone;
      this.classList.remove('is-toning');
      // Restart a brief tonal wash so the environment changes with the selected finish.
      void this.offsetWidth;
      this.classList.add('is-toning');
    }
    const mobileSource = this.querySelector('[data-finale-mobile-source]');
    const mobileSrc = button.dataset.finaleMobileSrc || '';
    if (mobileSource) {
      if (mobileSrc) mobileSource.srcset = mobileSrc;
      else mobileSource.removeAttribute('srcset');
    }
    this.dataset.mobilePortrait = String(Boolean(mobileSrc));
    const image = this.querySelector('[data-finale-image]');
    const stickyImage = this.querySelector('[data-sticky-image]');
    const requestId = this.sceneRequest = (this.sceneRequest || 0) + 1;
    if (!image || image.src === button.dataset.finaleSrc) {
      image?.classList.remove('is-changing');
      return;
    }
    image.classList.add('is-changing');
    const next = new Image();
    const nextSrc = button.dataset.finaleSrc;
    next.onload = () => {
      if (requestId !== this.sceneRequest) return;
      image.src = nextSrc;
      image.alt = button.dataset.finaleAlt;
      if (stickyImage) stickyImage.src = button.dataset.finaleSrc;
      requestAnimationFrame(() => image.classList.remove('is-changing'));
    };
    next.onerror = () => { if (requestId === this.sceneRequest) image.classList.remove('is-changing'); };
    next.src = nextSrc;
  }

  goToConfiguration() {
    document.getElementById('banzrc-gt-configuration')?.scrollIntoView({ behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block:'start' });
  }

  purchase() {
    if (!this.state.ready) return this.goToConfiguration();
    const nativeButton = this.purchaseSection?.querySelector('add-to-cart-component button');
    if (nativeButton && !nativeButton.disabled) nativeButton.click();
  }

  openChat() {
    const chat = document.querySelector('shopify-chat');
    if (!chat) return;
    if (!window.matchMedia('(max-width: 749px)').matches) {
      const activator = chat.shadowRoot?.querySelector('[part="activator"]');
      if (activator instanceof HTMLElement) activator.click();
      return;
    }
    this.dataset.mobileChatOpen = 'true';
    requestAnimationFrame(() => {
      const show = () => {
        if (this.isConnected && chat.isConnected && typeof chat.show === 'function') chat.show();
      };
      if (typeof chat.show === 'function') show();
      else customElements.whenDefined('shopify-chat').then(show);
    });
  }

  updateSticky() {
    const sticky = this.querySelector('[data-finale-sticky]');
    const visible = Boolean(this.purchasePassed && !this.finaleVisible);
    sticky.hidden = !visible;
    requestAnimationFrame(() => sticky.classList.toggle('is-visible', visible));
  }

  disconnectedCallback() {
    this.chatStateObserver?.disconnect();
    this.configObserver?.disconnect();
    this.purchaseObserver?.disconnect();
    this.finaleObserver?.disconnect();
  }
}
if (!customElements.get('banzrc-gt-finale')) customElements.define('banzrc-gt-finale', BanzrcGtFinale);
